// The strip over a session's transcript: what it is and what it is doing,
// then what it runs on. Its title, its state, its issue with the state the
// board holds for it, and End, which ends it; then its agent and model, its
// runner, the commit it started from, and who started it when. A worker a
// lead delegated has a button back to its lead, before its title, and an
// icon that minimises it into its lead's side pane. Moved from today's Code
// tab (code/StatusStrip.tsx there): confirmed model/effort/mode and the
// reported token context remain observations; the runner, the base
// commit, who started it, and the proposal a review session reviews are the
// new server's, and the branch went, since each session has a worktree of its
// own. Under them, the runner it waits on while that runner is offline
// (Poulpe-367).
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { Issue, Me, Runner, Session } from '../api/types'
import { exactTime, longAge } from '../components/Time'
import { useSessionPath } from '../projects/ProjectRoute'
import { proposalPath } from '../queue/words'
import { useBoardState, useStore } from '../store/context'
import { needsServer } from '../components/needsServer'
import { SessionWaitsOn } from '../runners/waits'
import { endedText, starterName } from '../sessions/words'
import { cx } from '../transcript/bits'
import { StateChip, StateDot } from './bits'
import { agentLabel, leadOf, shownState, stateText } from './format'
import { ListIcon, MinimiseIcon, SidePaneIcon } from './icons'
import { Context } from './Context'
import { Cache, Spent, Turns, cacheExpiry } from './Usage'
import { choiceLabel, choices, currentEffort, effortOption, modelChoices, modeChoices } from './choices'
import { BudgetSettings } from './BudgetSettings'
import './controls.css'

export interface StatusStripProps {
  session: Session
  /** The title the page gives it (format.ts). */
  title: string
  issue: Issue | undefined
  asking: boolean
  runners: readonly Runner[] | null
  me: Me | null
  paneOpen: boolean
  onTogglePane: () => void
  /** Where the list is a drawer: open it. */
  onShowList?: () => void
  /** End the session; absent where the pages cannot. */
  onEnd?: () => void
  /** He ended it and its runner has not yet: the strip says so, and the button waits. */
  ending?: boolean
  /** The title of the lead that delegated this one, when the page holds it. */
  leadTitle?: string
  /** When its agent last replied, from its transcript: its cache counts down from then. */
  replied?: string | null
}

/** The longest title the server keeps. */
const TITLE_MAX = 120

/**
 * The session's title, as a button: a click puts a box in its place; Enter or leaving the box saves it
 * through the outbox, Escape leaves it as it was, and an empty one reverts. Offline it still saves:
 * the outbox keeps it.
 */
function Title({ session, title }: { session: Session; title: string }) {
  const store = useStore()
  const [draft, setDraft] = useState<string | null>(null)
  // Enter and Escape take the box away, which a browser may say as a blur: it is settled once.
  const settled = useRef(false)
  const end = (save: boolean) => {
    if (settled.current || draft === null) return
    settled.current = true
    const next = draft.split(/\s+/).filter(Boolean).join(' ')
    setDraft(null)
    if (save && next !== '' && next !== title) void store.renameSession(session.id, next)
  }
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      end(true)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      end(false)
    }
  }
  if (draft !== null) {
    return (
      <h1 className="code-title is-editing">
        <input
          type="text"
          className="code-title-input"
          value={draft}
          maxLength={TITLE_MAX}
          aria-label="Session title"
          autoFocus
          autoComplete="off"
          onFocus={(event) => event.target.select()}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => end(true)}
        />
      </h1>
    )
  }
  return (
    <h1 className="code-title">
      <button
        type="button"
        className="code-title-button"
        title={`${title || 'Untitled session'}\nClick to rename`}
        onClick={() => {
          settled.current = false
          setDraft(title)
        }}
      >
        {title || 'Untitled session'}
      </button>
    </h1>
  )
}

/** The issue a session is on: its key, linked, with the state the board holds for it. */
function SessionIssue({ issue }: { issue: Issue }) {
  return (
    <Link className="code-strip-issue" to={`/${encodeURIComponent(issue.key)}`} title={issue.title}>
      <span className="request-issue-key">{issue.key}</span>
      <StateChip state={issue.state} />
    </Link>
  )
}

export function StatusStrip({ session, title, issue, asking, runners, me, paneOpen, onTogglePane, onShowList, onEnd, ending = false, leadTitle, replied = null }: StatusStripProps) {
  const pathOf = useSessionPath()
  const { offline, projects } = useBoardState()
  const agent = agentLabel(session, runners)
  const settings = session.settings
  const model = settings ? choiceLabel(modelChoices(settings), settings.current_model) : null
  const effort = settings ? currentEffort(settings, agent.id) : null
  const effortChoices = settings ? choices(effortOption(settings.options, agent.id)?.choices) : []
  // An effort offered but not reported is the agent's default.
  const effortName = effort ? choiceLabel(effortChoices, effort.value) : effortChoices.length > 0 ? 'Default' : null
  const mode = settings ? modeChoices(settings.modes).find(choice => choice.id === settings.current_mode)?.label ?? null : null
  const lead = leadOf(session)
  const runner = runners?.find((r) => r.id === session.runner_id)
  const base = session.origin.base
  const metrics: ReactNode[] = [
    <span className="code-metric code-model" key="model" title={session.agent.vendor}>
      <span className={cx('tag', agent.id && `is-${agent.id}`)}>
        <span className="code-engine-dot" aria-hidden="true" />
        {agent.name}
      </span>{' '}
      {model && <span className="code-model-more">{model}</span>}
    </span>,
    <span className="code-metric" key="runner" title={runner?.machine ?? undefined}>
      <span className="code-metric-name">Runner</span> {runners === null ? '…' : (runner?.name ?? session.runner_id)}
    </span>,
    <span className="code-metric" key="base" title={base ?? undefined}>
      <span className="code-metric-name">Base</span> {base ? <code>{base.slice(0, 12)}</code> : 'Not known yet'}
    </span>,
    <span className="code-metric" key="started" title={exactTime(session.created_at)}>
      <span className="code-metric-name">Started</span> {longAge(session.created_at, Date.now())} by {starterName(session.started_by, me)}
    </span>,
  ]
  if (effortName) metrics.splice(1, 0, <span className="code-metric" key="effort"><span className="code-metric-name">Effort</span> {effortName}</span>)
  if (mode) metrics.splice(effortName ? 2 : 1, 0, <span className="code-metric" key="mode"><span className="code-metric-name">Mode</span> {mode}</span>)
  // What it used, after what it runs as, in today's order: its context, its cache, its turns; then its tokens, and its price on an API key.
  const expires = cacheExpiry(session, agent.id, replied)
  const used: ReactNode[] = []
  if (session.usage?.context) used.push(<Context key="context" session={{engine: agent.id ?? '', context: session.usage.context}}/>)
  if (expires !== null) used.push(<Cache key="cache" expires={expires} />)
  if (session.usage?.prompt_requests) used.push(<Turns key="turns" usage={session.usage} />)
  if (session.usage?.new_tokens !== undefined || (agent.api && session.usage?.cost)) used.push(<Spent key="spent" usage={session.usage} api={agent.api} />)
  metrics.splice(1 + Number(!!effortName) + Number(!!mode), 0, ...used)
  if (session.allowance) {
    const a = session.allowance
    const parts = a.budget ? [a.budget.turns !== null && `${a.turns}/${a.budget.turns} turns`, a.budget.minutes !== null && `${a.budget.minutes} min`].filter(Boolean) : a.limits ? [`${a.limits.working_minutes} min working`, `${a.limits.lifetime_minutes} min lifetime`] : []
    metrics.push(<span className="code-metric" key="budget" title={a.stopped ?? 'What it may spend before it stops'}><span className="code-metric-name">Budget</span> {parts.join(' · ')}{a.stopped && <strong> · spent</strong>}</span>)
    if (a.stopped) metrics.push(<span className="code-metric text-danger" role="status" key="limit-reason">{a.stopped}</span>)
    if (a.token_warned) metrics.push(<span className="code-metric" key="token-warning">This worker reached its token warning.</span>)
  }
  if (session.ended) {
    metrics.push(
      <span className="code-metric" key="ended">
        <span className="code-metric-name">Ended</span> {endedText(session.ended)}
      </span>,
    )
  }
  // A session the landing queue started to review a proposal links to it on its project's Board (Poulpe-378, Poulpe-406).
  if (session.origin.kind === 'review') {
    metrics.splice(
      1,
      0,
      <span className="code-metric" key="origin">
        <span className="code-metric-name">Origin</span> <Link to={proposalPath(session.origin.proposal, projects.get(session.project_id)?.key)}>Review of a proposal</Link>
      </span>,
    )
  }
  return (
    <header className="code-strip">
      <div className="code-strip-top">
        {onShowList && (
          <button type="button" className="button button-quiet code-icon-button code-list-button" aria-label="Sessions" title="Sessions" onClick={onShowList}>
            <ListIcon size={15} />
          </button>
        )}
        {/* Drawn as a button, so that it is seen; it is the link to the lead it was. */}
        {lead && (
          <Link className="button button-quiet button-small code-strip-back" to={pathOf(lead)} aria-label="Back to lead session" title={leadTitle}>
            ‹ {leadTitle || 'Lead session'}
          </Link>
        )}
        <StateDot session={session} asking={asking} />
        <Title session={session} title={title} />
        <span className={cx('code-state-text', !ending && `is-${shownState(session, asking)}`)}>{ending ? 'Ending…' : stateText(session, asking)}</span>
        {issue && <SessionIssue issue={issue} />}
        <span className="code-strip-actions">
          {session.origin.kind === 'started' && <BudgetSettings key={JSON.stringify([me?.workspace.id, me?.member?.id, session.id])} session={session} />}
          {/* The pair of Maximise in the pane: the lead's page is told which worker to show open there. */}
          {lead && (
            <Link
              className="button button-quiet code-icon-button"
              to={pathOf(lead)}
              state={{ subagent: session.id }}
              aria-label="Minimise"
              title="Minimise: back into the side pane of its lead"
            >
              <MinimiseIcon size={15} />
            </Link>
          )}
          {onEnd && (
            <button
              type="button"
              className="button button-quiet button-small"
              aria-label="End session"
              disabled={ending || offline}
              onClick={onEnd}
              title={ending ? 'Its runner is ending it: that takes some seconds.' : 'End the session: its agent stops, and it takes no more prompts.'}
              {...needsServer(offline)}
            >
              {ending ? 'Ending…' : 'End'}
            </button>
          )}
          <button
            type="button"
            className={cx('button button-quiet code-icon-button', paneOpen && 'is-active')}
            aria-label="Side pane"
            aria-pressed={paneOpen}
            title={paneOpen ? 'Hide the side pane' : 'Show the side pane: Changes, Issue, SubAgents'}
            onClick={onTogglePane}
          >
            <SidePaneIcon size={15} />
          </button>
        </span>
      </div>
      <div className="code-metrics" role="group" aria-label="The session’s numbers">
        {metrics}
      </div>
      <SessionWaitsOn session={session} />
    </header>
  )
}
